import { useMemo } from 'react';
import * as Adflow from 'adflow';
import { useParams } from 'react-router-dom';
import { APIClientContext as FigmaAPIClientContext } from '@adflow/figma';
import { APIClientContext as SelectAdvertiserAPIClientContext } from '@adflow/marketing-cloud';
import { APIClientContext as ShadowBrandAPIClientContext } from '@adflow/shadow-brand';
import { Datasources, EditorState } from '@adflow/types';
import { TRPCClient } from '../utils/apiClient';
import { useAdTemplateData } from '../utils/hooks/useAdTemplateData';
import { APIClientContext as CreatomateAPIContextProvider } from '@adflow/creatomate';
import { CustomAlert } from '../components/customAlert';
import { useEditorModules } from '../utils/hooks/useEditorModules';

function Duplicate() {
  const { modules } = useEditorModules();
  const { id: idParam } = useParams<{ id: string | undefined }>();
  const id = idParam || '';

  // Pull everything from your custom hook
  const {
    adTemplate,
    brand,
    brandKit,
    fonts,
    ready,
    warningOpen,
    warningMessage,
    setWarningOpen,
    hasEditPermissions,
  } = useAdTemplateData(id);

  // Keep the logic for defaultStoreValues
  const defaultStoreValues = useMemo<Partial<EditorState>>(() => {
    if (!adTemplate || !fonts) return {};

    // preserve current config from shadow brand
    let teamAssetsConfig = adTemplate.displayOptions?.teamAssets;
    if (!teamAssetsConfig) {
      teamAssetsConfig = {
        type: 'JERSEY',
        fallback: 'NONE',
      };
    }

    return {
      ...adTemplate,
      name: adTemplate.name + '_DUPLICATE',
      elements: adTemplate.adElements,
      brand,
      brandKit,
      brandKitId: adTemplate.brandKitId || null,
      customFonts: fonts,
      teamAssets: teamAssetsConfig,
      thumbnailURL: adTemplate.thumbnail || '', // Map thumbnail to thumbnailURL for editor
    };
  }, [adTemplate, brand, brandKit, fonts]);

  const dataSources = useMemo(() => {
    return Datasources.createDataSourceDefinitions(adTemplate?.dataSources || null);
  }, [adTemplate]);

  const renderPreview = (args: Adflow.RenderPreviewArgs) => {
    return args.importSourceType === 'creatomate' && args.importSourceJSON ? (
      <Adflow.CreatomatePreview
        source={args.importSourceJSON}
        elements={args.elements}
        brand={args.brand}
        displayOptions={args.displayOptions}
      />
    ) : (
      <Adflow.Preview
        elements={args.elements}
        brand={args.brand}
        brandKit={args.brandKit}
        fonts={args.fonts}
        displayOptions={args.displayOptions}
        selectedElementId={args.selectedElementId}
        isIndexPreview={false}
      />
    );
  };

  const onSave = async (editorState: EditorState) => {
    // Use the editor's thumbnail if generated, otherwise use the original template's thumbnail
    const thumbnail = editorState.thumbnailURL || adTemplate?.thumbnail || null;

    await TRPCClient.client.adTemplate.create.mutate({
      name: editorState.name,
      brandId: editorState.brand?._id,
      brandKitId: editorState.brandKitId,
      dataSources: editorState.dataSources,
      adElements: editorState.elements,
      displayOptions: editorState.displayOptions,
      thumbnail,
      businessEntityId: editorState.businessEntityId,
      importSourceJSON: editorState.importSourceJSON,
      importSourceType: editorState.importSourceType,
      videoProjectId: editorState.videoProjectId,
    });
  };

  if (!ready) {
    if (!hasEditPermissions) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'start',
            height: '100vh',
            width: '100%',
            padding: '4rem 2rem',
          }}
        >
          <CustomAlert
            type="error"
            title="Unauthorized"
            message="You do not have the permissions to access this page"
            closable={false}
          />
        </div>
      );
    }
    return <span>Loading</span>;
  }

  if (warningOpen) {
    return (
      <Adflow.WarningModal
        isOpen={warningOpen}
        onClose={() => setWarningOpen(false)}
        warning={warningMessage}
        title="Warning"
      ></Adflow.WarningModal>
    );
  }

  return (
    <div className="container" style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
      <Adflow.EditorProvider dataSources={dataSources} defaultStoreValues={defaultStoreValues}>
        <FigmaAPIClientContext.Provider {...TRPCClient.figmaClient}>
          <ShadowBrandAPIClientContext.Provider {...TRPCClient.consoleClient}>
            <SelectAdvertiserAPIClientContext.Provider {...TRPCClient.marketingCloudClient}>
              <CreatomateAPIContextProvider.Provider {...TRPCClient.creatomateClient}>
                <Adflow.Editor
                  saveButtonVisible={hasEditPermissions}
                  onSave={onSave}
                  modules={modules}
                  renderPreview={renderPreview}
                ></Adflow.Editor>
              </CreatomateAPIContextProvider.Provider>
            </SelectAdvertiserAPIClientContext.Provider>
          </ShadowBrandAPIClientContext.Provider>
        </FigmaAPIClientContext.Provider>
      </Adflow.EditorProvider>
    </div>
  );
}
export default Duplicate;
